import { addDays, localDay } from '@quickreport/domain';
import { Badge, Button, EmptyState, Input, PillTabs } from '@quickreport/ui';
import { completeReminder, delayReport, type Reminder, setDueDate } from '@quickreport/workspace';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { AlarmClock, Check, ExternalLink, Info, Megaphone, Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { describeError } from '../../lib/api';
import { type Message, useNotifications } from '../../lib/notifications';
import { useWorkspace } from '../../lib/workspace';
import { STATUS_LABEL, STATUS_TONE, useCtx } from '../reports/context';

type Tab = 'reminders' | 'messages';

export function NotificationsPage() {
  const n = useNotifications();
  const [tab, setTab] = useState<Tab>(() =>
    n.unreadReminders === 0 && n.unreadMessages > 0 ? 'messages' : 'reminders',
  );
  const [toast, setToast] = useState<{ text: string; undo?: () => Promise<void> } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 9000);
    return () => clearTimeout(t);
  }, [toast]);

  const unread = n.unread;
  const allKeys = [...n.reminders.map((r) => r.key), ...n.messages.map((m) => m.key)];

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Notifications</h1>
          <p className="mt-1 text-slate">
            {n.reminders.length === 0
              ? 'No report is due today.'
              : `${n.reminders.length} report${n.reminders.length === 1 ? ' needs' : 's need'} attention today.`}
            {n.messages.length
              ? ` ${n.messages.length} message${n.messages.length === 1 ? '' : 's'} from the LabView team.`
              : ''}
          </p>
        </div>
        {unread > 0 ? (
          <Button onClick={() => void n.markRead(allKeys)}>
            <Check size={15} /> Mark all as read
          </Button>
        ) : null}
      </div>

      <div className="mt-5">
        <PillTabs
          label="Notifications"
          value={tab}
          onChange={setTab}
          tabs={[
            {
              value: 'reminders',
              label: `Reminders${n.unreadReminders ? ` (${n.unreadReminders})` : ''}`,
            },
            {
              value: 'messages',
              label: `Messages${n.unreadMessages ? ` (${n.unreadMessages})` : ''}`,
            },
          ]}
        />
      </div>

      {error ? (
        <p role="alert" className="mt-4 rounded-md bg-red-50 px-3 py-2 text-[13px] text-flag">
          {error}
        </p>
      ) : null}

      {tab === 'reminders' ? <Reminders onError={setError} onToast={setToast} /> : <Messages />}

      {toast ? (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-xl bg-navy px-4 py-2.5 text-[13px] text-white shadow-lg"
        >
          {toast.text}
          {toast.undo ? (
            <button
              type="button"
              className="font-semibold underline"
              onClick={() => {
                const undo = toast.undo;
                setToast(null);
                void undo?.().catch((e) => setError(describeError(e)));
              }}
            >
              Undo
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Reminders({
  onError,
  onToast,
}: {
  onError: (m: string | null) => void;
  onToast: (t: { text: string; undo?: () => Promise<void> } | null) => void;
}) {
  const n = useNotifications();
  const ws = useWorkspace();
  const ctx = useCtx();
  const [delaying, setDelaying] = useState<string | null>(null);

  const overdue = n.reminders.filter((r) => r.bucket === 'overdue');
  const today = n.reminders.filter((r) => r.bucket === 'today');

  async function run(fn: () => Promise<void>) {
    onError(null);
    try {
      await fn();
      n.refresh();
      void ws.engine.syncNow();
    } catch (e) {
      onError(describeError(e));
    }
  }

  const complete = (r: Reminder) =>
    run(async () => {
      await completeReminder(ws.db, ctx, r.reportId);
      await n.markRead([r.key]);
      onToast({
        text: `${r.patient || r.labNo} marked complete`,
        undo: async () => {
          await setDueDate(ws.db, ctx, r.reportId, r.dueDate);
          await n.markUnread([r.key]);
          n.refresh();
        },
      });
    });

  const delay = (r: Reminder, day: string) =>
    run(async () => {
      await delayReport(ws.db, ctx, r.reportId, day);
      setDelaying(null);
      onToast({
        text: `${r.patient || r.labNo} moved to ${new Date(`${day}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`,
        undo: async () => {
          await setDueDate(ws.db, ctx, r.reportId, r.dueDate);
          n.refresh();
        },
      });
    });

  if (n.reminders.length === 0) {
    return (
      <EmptyState title="Nothing is due today">
        When you promise a report for today, it appears here, and again if it is late. Signed
        reports leave this list on their own.
      </EmptyState>
    );
  }

  const card = (r: Reminder) => (
    <li
      key={r.key}
      className={clsx(
        'rounded-xl border bg-paper p-4',
        r.bucket === 'overdue' ? 'border-red-200' : 'border-line',
        !n.isRead(r.key) && 'shadow-sm ring-1 ring-accent/30',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={clsx(
            'mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full',
            n.isRead(r.key) ? 'bg-transparent' : 'bg-accent',
          )}
          title={n.isRead(r.key) ? 'Read' : 'Not read yet'}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{r.patient || 'Patient'}</span>
            <Badge tone={STATUS_TONE[r.status] ?? 'neutral'}>
              {STATUS_LABEL[r.status] ?? r.status}
            </Badge>
            <span className="font-mono text-[12px] text-slate">{r.labNo}</span>
          </div>
          <div className="mt-0.5 truncate text-[13px] text-slate" title={r.name}>
            {r.name}
          </div>
          <div
            className={clsx(
              'mt-1.5 flex items-center gap-1.5 text-[13px] font-medium',
              r.bucket === 'overdue' ? 'text-flag' : 'text-warn',
            )}
          >
            <AlarmClock size={14} />
            {r.bucket === 'overdue' ? `Overdue: was due ${r.when}` : 'Due today'}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2 pl-5">
        <Link
          to="/reports/$reportId"
          params={{ reportId: r.reportId }}
          onClick={() => void n.markRead([r.key])}
          className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-navy px-3 text-[13px] font-medium text-white hover:bg-navy-hover"
        >
          <ExternalLink size={13} /> Open report
        </Link>
        <Button
          size="sm"
          onClick={() => void complete(r)}
          title="The work is finished. This takes it off the list."
        >
          <Check size={13} /> Mark complete
        </Button>
        <Button
          size="sm"
          onClick={() => setDelaying(delaying === r.key ? null : r.key)}
          aria-expanded={delaying === r.key}
        >
          <AlarmClock size={13} /> Delay
        </Button>
        <Button
          size="sm"
          tone="ghost"
          onClick={() => void (n.isRead(r.key) ? n.markUnread([r.key]) : n.markRead([r.key]))}
        >
          {n.isRead(r.key) ? 'Mark unread' : 'Mark read'}
        </Button>
      </div>

      {delaying === r.key ? (
        <DelayPanel
          report={r}
          onPick={(d) => void delay(r, d)}
          onCancel={() => setDelaying(null)}
        />
      ) : null}
    </li>
  );

  return (
    <div className="mt-5 grid gap-6">
      {overdue.length ? (
        <section aria-label="Overdue">
          <h2 className="mb-2 text-[13px] font-semibold tracking-wide text-flag uppercase">
            Overdue · {overdue.length}
          </h2>
          <ul className="grid gap-3">{overdue.map(card)}</ul>
        </section>
      ) : null}
      {today.length ? (
        <section aria-label="Due today">
          <h2 className="mb-2 text-[13px] font-semibold tracking-wide text-slate uppercase">
            Due today · {today.length}
          </h2>
          <ul className="grid gap-3">{today.map(card)}</ul>
        </section>
      ) : null}
    </div>
  );
}

/** Choose the new date: a quick choice, or any later day. */
function DelayPanel({
  report,
  onPick,
  onCancel,
}: {
  report: Reminder;
  onPick: (day: string) => void;
  onCancel: () => void;
}) {
  const today = localDay();
  const [day, setDay] = useState('');
  const quick: [string, number][] = [
    ['Tomorrow', 1],
    ['In 2 days', 2],
    ['In a week', 7],
  ];
  return (
    <fieldset className="mt-3 ml-5 grid gap-3 rounded-lg border-0 bg-canvas p-3">
      <legend className="sr-only">New date for {report.patient || report.labNo}</legend>
      <div className="text-[13px] font-medium">When will it be ready?</div>
      <div className="flex flex-wrap gap-2">
        {quick.map(([label, d]) => (
          <Button key={label} size="sm" onClick={() => onPick(addDays(today, d))}>
            {label}
          </Button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="date"
          aria-label="Pick a date"
          min={addDays(today, 1)}
          value={day}
          onChange={(e) => setDay(e.target.value)}
          className="w-44"
        />
        <Button
          tone="primary"
          size="sm"
          disabled={!day || day <= today}
          onClick={() => onPick(day)}
        >
          Set new date
        </Button>
        <Button size="sm" tone="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </fieldset>
  );
}

const KIND = {
  info: { icon: Info, box: 'border-line', chip: 'neutral' as const, label: 'Message' },
  payment: { icon: Wallet, box: 'border-amber-200', chip: 'brand' as const, label: 'Payment' },
  urgent: { icon: Megaphone, box: 'border-red-200', chip: 'flag' as const, label: 'Important' },
};

function Messages() {
  const n = useNotifications();
  if (n.messages.length === 0)
    return (
      <EmptyState title="No messages">
        Messages from the LabView team, such as payment reminders and notices, appear here.
      </EmptyState>
    );
  return (
    <ul className="mt-5 grid gap-3">
      {n.messages.map((m: Message) => {
        const k = KIND[m.kind] ?? KIND.info;
        const Icon = k.icon;
        const read = n.isRead(m.key);
        return (
          <li
            key={m.key}
            className={clsx(
              'rounded-xl border bg-paper p-4',
              k.box,
              !read && 'shadow-sm ring-1 ring-accent/30',
            )}
          >
            <div className="flex items-start gap-3">
              <span
                className={clsx(
                  'mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full',
                  read ? 'bg-transparent' : 'bg-accent',
                )}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Icon size={15} className="text-slate" />
                  <span className="font-semibold">{m.title}</span>
                  <Badge tone={k.chip}>{k.label}</Badge>
                </div>
                {m.body ? (
                  <p className="mt-1.5 text-[14px] whitespace-pre-wrap text-ink">{m.body}</p>
                ) : null}
                <div className="mt-2 text-[12px] text-slate">
                  {new Date(m.createdAt).toLocaleString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </div>
              </div>
              <Button
                size="sm"
                tone="ghost"
                onClick={() => void (read ? n.markUnread([m.key]) : n.markRead([m.key]))}
              >
                {read ? 'Mark unread' : 'Mark read'}
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
